import {FormEventHandler, forwardRef, ReactNode} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'

import Checkbox from 'components/elements/fields/Checkbox'
import MobxSelect from 'components/elements/fields/MobxSelect'
import MobxTextInput from 'components/elements/fields/MobxTextInput'
import {Col, Row} from 'components/elements/grid'
import {maritalOptions} from 'models/PayStub/PayStub.configs'
import usStates from 'src/configs/usStates'
import {usePayStubStore} from 'src/utils/contexts/RootStore.context'

import s from './FieldsPayStubEmployeeInfo.module.scss'

const cx = cn.bind(s)

interface IFieldsPayStubEmployeeInfoProps {
	className?: string
	children?: ReactNode | string
	onChange?: FormEventHandler<HTMLDivElement>
}

const FieldsPayStubEmployeeInfo = forwardRef<HTMLDivElement, IFieldsPayStubEmployeeInfoProps>(
	({className, children, ...props}, ref) => {
		const payStubStore = usePayStubStore()
		const {
			employeeInfo: {isDirectDeposit},
			setEmployeeInfo,
		} = payStubStore

		return (
			<div className={cx('employee', className)} data-testid="employee-info" {...props} ref={ref}>
				{children}
				<div className={cx('employee--fields')}>
					<Row className={cx('employee__row')}>
						<Col md={6}>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={'Employee name'}
								placeholder="Your Full Name"
								store={payStubStore.employeeInfo}
								name="employeeName"
								required
								variant="editable"
							/>
						</Col>
						<Col md={6}>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={'Employee telephone number'}
								placeholder="123-456-7890"
								store={payStubStore.employeeInfo}
								name="employeeTelephone"
								variant="editable"
							/>
						</Col>
					</Row>
					<Row className={cx('employee__row')}>
						<Col md={6}>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={'Employee social'}
								placeholder="Last 4 Digits"
								store={payStubStore.employeeInfo}
								onChange={(e) => setEmployeeInfo(e.target.value)}
								name="employeeSocial"
								variant="editable"
							/>
						</Col>
						<Col md={6}>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={'Employee id'}
								placeholder="1234"
								store={payStubStore.employeeInfo}
								name="employeeId"
								variant="editable"
							/>
						</Col>
					</Row>
					<MobxTextInput
						wrapperClassName={cx('field')}
						label={'Employee address 1'}
						placeholder="Your Full Address"
						store={payStubStore.employeeInfo}
						name="streetAddress1"
						required
						variant="editable"
					/>
					<MobxTextInput
						wrapperClassName={cx('field')}
						label={'employee address 2'}
						placeholder="Suite 1234"
						store={payStubStore.employeeInfo}
						name="streetAddress2"
						variant="editable"
					/>
					<Row className={cx('employee__row')}>
						<Col md={4}>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={'City'}
								placeholder="Your City"
								store={payStubStore.employeeInfo}
								name="city"
								required
								variant="editable"
							/>
						</Col>
						<Col md={4}>
							<MobxSelect
								name="state"
								placeholder="Choose state"
								label="State"
								store={payStubStore.employeeInfo}
								wrapperClassName={cx('field')}
								data-testid="state"
								required
								onFocus={payStubStore.pauseCalculation}
								onBlurCapture={payStubStore.resumeCalculation}
								onChange={(e) => {
									payStubStore.employeeInfo.state = e.target.value
								}}
								variant="editable"
								options={usStates}
							/>
						</Col>
						<Col md={4}>
							<MobxTextInput
								wrapperClassName={cx('zip-field', 'field')}
								label={'Zip code'}
								placeholder="12345"
								store={payStubStore.employeeInfo}
								name="zipCode"
								required
								variant="editable"
								maxLength={5}
							/>
						</Col>
					</Row>
					<Row className={cx('employee__row')}>
						<Col md={6}>
							<MobxSelect
								name="maritalStatus"
								label="Marital status"
								store={payStubStore.employeeInfo}
								options={maritalOptions}
								onFocus={payStubStore.pauseCalculation}
								onBlurCapture={payStubStore.resumeCalculation}
								onChange={(e) => {
									payStubStore.employeeInfo.maritalStatus = e.target
										.value as calculator.TFederalFilingStatus
								}}
								wrapperClassName={cx('marital-field', 'field')}
							/>
						</Col>
						<Col md={6}>
							<MobxTextInput
								name="dependentsAmount"
								label="Dependents amount"
								type="number"
								step="0.01"
								min="0"
								store={payStubStore.employeeInfo}
								wrapperClassName={cx('exemptions-field', 'field')}
								onFocus={payStubStore.pauseCalculation}
								onBlurCapture={payStubStore.resumeCalculation}
							/>
						</Col>
					</Row>
					<div className={cx('deposit')}>
						<div>
							<p className={cx('deposit__text')}>Direct deposit</p>
							<div className={cx('deposit__wrapper')}>
								<Checkbox
									classNameWrapper={cx('deposit__check')}
									label="Yes"
									type="radio"
									name="isDirectDeposit"
									onChange={() => {
										payStubStore.employeeInfo.isDirectDeposit = true
									}}
									value={payStubStore.employeeInfo.isDirectDeposit === true}
								/>
								<Checkbox
									classNameWrapper={cx('deposit__check')}
									label="No"
									type="radio"
									name="isDirectDeposit"
									onChange={() => {
										payStubStore.employeeInfo.isDirectDeposit = false
									}}
									value={payStubStore.employeeInfo.isDirectDeposit === false}
								/>
							</div>
						</div>
						{isDirectDeposit && (
							<MobxTextInput
								wrapperClassName={cx('digital-field', 'field')}
								label={'Last 4 Digits of Bank Checking Account'}
								placeholder="XXXX"
								store={payStubStore.employeeInfo}
								onChange={(e) => {
									const value = e.target.value
									payStubStore.employeeInfo.last4Digits = value.slice(0, 4)
								}}
								name="last4Digits"
								type="number"
								maxLength={4}
								min="1"
								max="9999"
							/>
						)}
					</div>
				</div>
			</div>
		)
	},
)

FieldsPayStubEmployeeInfo.displayName = 'FieldsPayStubEmployeeInfo'

export default observer(FieldsPayStubEmployeeInfo)
